import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function MassCoreTempSlider() {
  const [mass, setMass] = useState<number>(1);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    // Clear background to #0F172A
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    const cxLeft = width * 0.35;
    const cyLeft = height / 2 - 15;

    const cxRight = width * 0.75;
    const cyRight = height / 2 - 15;

    // 1. Calculations
    const starRadius = 20 + (mass / 50) * 80;

    // Helper to interpolate colors
    const parseHex = (hex: string) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    const formatHex = (r: number, g: number, b: number) => {
      return `#${((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1)}`;
    };

    const interpolate = (color1: string, color2: string, factor: number) => {
      const c1 = parseHex(color1);
      const c2 = parseHex(color2);
      const r = c1.r + (c2.r - c1.r) * factor;
      const g = c1.g + (c2.g - c1.g) * factor;
      const b = c1.b + (c2.b - c1.b) * factor;
      return formatHex(r, g, b);
    };

    let starColor = '#FF6B35';
    if (mass < 2) {
      const factor = Math.max(0, Math.min(1, (mass - 0.1) / (2 - 0.1)));
      starColor = interpolate('#FF6B35', '#FFD700', factor);
    } else if (mass < 8) {
      const factor = Math.max(0, Math.min(1, (mass - 2) / (8 - 2)));
      starColor = interpolate('#FFD700', '#FFFFFF', factor);
    } else if (mass < 20) {
      const factor = Math.max(0, Math.min(1, (mass - 8) / (20 - 8)));
      starColor = interpolate('#FFFFFF', '#60A5FA', factor);
    } else {
      const factor = Math.max(0, Math.min(1, (mass - 20) / (50 - 20)));
      starColor = interpolate('#60A5FA', '#3B82F6', factor);
    }

    // Star type label
    let starType = 'Sun-like';
    if (mass < 0.5) starType = 'Red Dwarf';
    else if (mass < 2) starType = 'Sun-like';
    else if (mass < 8) starType = 'Subgiant';
    else if (mass < 20) starType = 'Blue Giant';
    else starType = 'Blue Supergiant';

    // Core temperature (Kelvin)
    const temp = Math.pow(10, 6 + mass / 10);
    const tempMillions = temp / 1e6;
    const tempStr = tempMillions < 10
      ? `${tempMillions.toFixed(1)}M °C`
      : `${Math.round(tempMillions).toLocaleString()}M °C`;

    // Fill level: log scale (Math.log10(temp/1e6) / 3 * 160)
    const logVal = Math.log10(temp / 1e6);
    const fillHeight = Math.max(0, Math.min(160, (logVal / 3) * 160));

    // Lifespan
    const lifespan = 10 / (mass * mass);
    let lifespanStr = '';
    let lifespanColor = '';
    if (lifespan < 0.1) {
      lifespanStr = `Lifespan: ~${Math.round(lifespan * 1000)} Million Years`;
      lifespanColor = '#EF4444'; // red
    } else if (lifespan < 1.0) {
      lifespanStr = `Lifespan: ~${Math.round(lifespan * 1000)} Million Years`;
      lifespanColor = '#FBBF24'; // yellow
    } else {
      lifespanStr = `Lifespan: ~${lifespan.toFixed(lifespan >= 10 ? 0 : 1)} Billion Years`;
      lifespanColor = '#22C55E'; // green
    }

    // 2. Render Star (Left side)
    // Star outer radial glow
    const starGlow = ctx.createRadialGradient(cxLeft, cyLeft, 0, cxLeft, cyLeft, starRadius * 1.6);
    starGlow.addColorStop(0, starColor);
    starGlow.addColorStop(0.3, starColor);
    starGlow.addColorStop(1.0, 'rgba(15, 23, 42, 0)');
    ctx.fillStyle = starGlow;
    ctx.beginPath();
    ctx.arc(cxLeft, cyLeft, starRadius * 1.6, 0, Math.PI * 2);
    ctx.fill();

    // Star core (White glow overlay)
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(cxLeft, cyLeft, starRadius * 0.9, 0, Math.PI * 2);
    ctx.fill();

    // Animated corona (small radial spikes rotating, count = floor(mass/2)+4)
    const spikeCount = Math.floor(mass / 2) + 4;
    const rotateSpeed = totalTime * 0.25;
    ctx.strokeStyle = starColor;
    ctx.lineWidth = 2;
    for (let i = 0; i < spikeCount; i++) {
      const angle = rotateSpeed + (i * Math.PI * 2) / spikeCount;
      ctx.beginPath();
      ctx.moveTo(cxLeft + Math.cos(angle) * starRadius, cyLeft + Math.sin(angle) * starRadius);
      ctx.lineTo(cxLeft + Math.cos(angle) * (starRadius + 8), cyLeft + Math.sin(angle) * (starRadius + 8));
      ctx.stroke();
    }

    // Star Type text watermark above star
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 15px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(starType, cxLeft, cyLeft - starRadius - 20);

    // Lifespan indicator text below star
    ctx.fillStyle = lifespanColor;
    ctx.font = 'bold 14px sans-serif';
    ctx.fillText(lifespanStr, cxLeft, cyLeft + starRadius + 28);

    // 3. Render Core Temperature Gauge (Right side)
    const gx = cxRight - 10;
    const gy = cyRight - 80;
    const gWidth = 20;
    const gHeight = 160;

    // Thermometer background track
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.beginPath();
    ctx.roundRect(gx, gy, gWidth, gHeight, 6);
    ctx.fill();

    // Thermometer frame
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(gx, gy, gWidth, gHeight, 6);
    ctx.stroke();

    // Thermometer fill (bottom blue -> middle yellow -> top white)
    if (fillHeight > 0) {
      const fillGrad = ctx.createLinearGradient(gx, gy + gHeight, gx, gy);
      fillGrad.addColorStop(0, '#3B82F6'); // blue
      fillGrad.addColorStop(0.5, '#FBBF24'); // yellow
      fillGrad.addColorStop(1.0, '#FFFFFF'); // white
      ctx.fillStyle = fillGrad;
      ctx.beginPath();
      ctx.roundRect(gx + 2, gy + gHeight - fillHeight + 2, gWidth - 4, fillHeight - 4, Math.min(4, fillHeight / 2));
      ctx.fill();
    }

    // Ticks and labels on the right
    const ticks = [
      { value: 1, label: '1M °C' },
      { value: 10, label: '10M °C' },
      { value: 100, label: '100M °C' },
      { value: 1000, label: '1B °C' }
    ];
    ticks.forEach(tick => {
      const h = (Math.log10(tick.value) / 3) * gHeight;
      const ty = gy + gHeight - h;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.beginPath();
      ctx.moveTo(gx + gWidth, ty);
      ctx.lineTo(gx + gWidth + 6, ty);
      ctx.stroke();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(tick.label, gx + gWidth + 10, ty + 3);
    });

    // Threshold lines
    // At threshold line (15M °C mark): dashed line labelled "Fusion ignition"
    const ignitionH = (Math.log10(15) / 3) * gHeight;
    const ignitionY = gy + gHeight - ignitionH;

    ctx.strokeStyle = '#EF4444';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(gx - 45, ignitionY);
    ctx.lineTo(gx + gWidth, ignitionY);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = '#EF4444';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('Fusion ignition', gx - 6, ignitionY + 3);

    // Temperature text value
    ctx.fillStyle = '#22D3EE';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(tempStr, cxRight + gWidth / 2, gy + gHeight + 35);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = 'bold 9px sans-serif';
    ctx.fillText('CORE TEMPERATURE', cxRight + gWidth / 2, gy - 20);

    // Top-Left Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Lesson: Astro G8 L05 - Star Mass vs. Temperature', 12, 24);
  }, [mass]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Slider bottom-centre */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1.5">
        <span className="text-white text-xs font-bold text-center">
          Star Mass: {mass.toFixed(1)} M☉
        </span>
        <input
          type="range"
          min="0.1"
          max="50"
          step="0.1"
          value={mass}
          onChange={(e) => setMass(parseFloat(e.target.value))}
          className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-white/20 rounded-lg appearance-none"
        />
      </div>
    </div>
  );
}
